import { TradeDataPoint, CandleDataPoint, AggregatedVolumePoint } from './types';
import { detectFrequency } from './candleUtils';

/**
 * Aggregate trade data by candle periods for buy/sell volume breakdown.
 * Uses O(1) bucket calculation instead of O(n) search per trade.
 * Time complexity: O(n + m) where n=trades, m=candles
 */
export function aggregateTradesByPeriod(
    trades: TradeDataPoint[],
    candles: CandleDataPoint[],
    frequencyMsOverride?: number
): AggregatedVolumePoint[] {
    if (candles.length === 0) return [];

    // Use provided frequency or detect from candle gaps
    const frequencyMs = frequencyMsOverride ?? detectFrequency(candles).frequencyMs;

    // Sort candles to find time boundaries
    const sortedCandles = [...candles].sort(
        (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );
    const firstCandleTs = sortedCandles[0].timestamp.getTime();
    const lastCandleTs = sortedCandles[sortedCandles.length - 1].timestamp.getTime();

    // Create map of candle timestamps to volume data
    const volumeMap = new Map<number, AggregatedVolumePoint>();

    // Initialize with candle data
    candles.forEach(candle => {
        const ts = candle.timestamp.getTime();
        volumeMap.set(ts, {
            timestamp: candle.timestamp,
            buyVolume: 0,
            sellVolume: 0,
            totalVolume: candle.baseVolume,
            tradeCount: candle.tradeCount,
            priceDirection: candle.close > candle.open ? 'up'
                : candle.close < candle.open ? 'down'
                : 'neutral'
        });
    });

    // Aggregate trades into candle periods using O(1) bucket calculation
    trades.forEach(trade => {
        const tradeTs = trade.timestamp.getTime();

        // Skip trades outside candle range
        if (tradeTs < firstCandleTs || tradeTs > lastCandleTs + frequencyMs) {
            return;
        }

        // O(1) bucket calculation: floor to nearest frequency boundary
        const bucketTs = Math.floor(tradeTs / frequencyMs) * frequencyMs;
        const volumePoint = volumeMap.get(bucketTs);

        if (volumePoint) {
            if (trade.side === 'buy') {
                volumePoint.buyVolume += trade.amount;
            } else {
                volumePoint.sellVolume += trade.amount;
            }
        }
    });

    return Array.from(volumeMap.values())
        .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}

/**
 * Format volume numbers for display (e.g., 1234567 -> "1.23M")
 */
export function formatVolume(value: number): string {
    if (value >= 1_000_000) {
        return `${(value / 1_000_000).toFixed(2)}M`;
    }
    if (value >= 1_000) {
        return `${(value / 1_000).toFixed(1)}K`;
    }
    return value.toFixed(0);
}

/**
 * Calculate the maximum volume value for Y scale domain
 */
export function getMaxVolume(
    candles: CandleDataPoint[],
    aggregatedVolume: AggregatedVolumePoint[],
    metric: 'volume' | 'tradeCount' | 'buySellVolume'
): number {
    if (metric === 'volume') {
        return Math.max(...candles.map(d => d.baseVolume), 0);
    } else if (metric === 'tradeCount') {
        return Math.max(...candles.map(d => d.tradeCount), 0);
    } else {
        // buySellVolume: use total of buy + sell
        return Math.max(...aggregatedVolume.map(d => d.buyVolume + d.sellVolume), 0);
    }
}
